// Sound engine with 5 different bilateral stimulation sounds
import logger from "../lib/logger";
// Stores user preferences in localStorage

// ============================================================================
// SECURE-RANDOM HELPERS (Sonar S2245)
// ============================================================================
// Sonar flags every `Math.random()` call as a potential CSPRNG violation,
// even in audio-noise contexts where it can't matter. Rather than sprinkle
// // NOSONAR everywhere, all random draws below go through these two helpers:
//   • `secureRandomFloat()` — single [0, 1) float via crypto.getRandomValues.
//   • `fillWhiteNoise(arr)` — bulk-fills a Float32Array with [-1, 1) values
//     using one getRandomValues call for the whole buffer. Same perf as a
//     Math.random() loop for the sample sizes we use (up to ~90k samples).
// Both fall through to Math.random() if Web Crypto is unavailable (only
// possible on very old non-secure contexts, which we don't ship to).
const _RAND_MAX = 0x100000000;
const _cryptoRef = typeof globalThis !== "undefined" ? globalThis.crypto : null;
function secureRandomFloat() {
  if (_cryptoRef && typeof _cryptoRef.getRandomValues === "function") {
    const buf = new Uint32Array(1);
    _cryptoRef.getRandomValues(buf);
    return buf[0] / _RAND_MAX;
  }
  return Math.random(); // NOSONAR — Web Crypto unavailable fallback
}
// Fills a Float32Array in-place with white-noise samples in [-1, 1). Uses
// a single crypto.getRandomValues call — the browser can back this with
// hardware entropy in bulk, so it's competitive with `Math.random()` in a
// tight loop even for 90k-sample buffers.
function fillWhiteNoise(target) {
  const len = target.length;
  if (_cryptoRef && typeof _cryptoRef.getRandomValues === "function") {
    // getRandomValues has a 65536-byte cap per call, so chunk larger fills.
    const chunkFloats = 16384; // 65536 bytes ÷ 4 bytes/uint32
    const scratch = new Uint32Array(Math.min(chunkFloats, len));
    let written = 0;
    while (written < len) {
      const remaining = len - written;
      const size = Math.min(scratch.length, remaining);
      const view = size === scratch.length ? scratch : scratch.subarray(0, size);
      _cryptoRef.getRandomValues(view);
      for (let i = 0; i < size; i += 1) {
        target[written + i] = view[i] / _RAND_MAX * 2 - 1;
      }
      written += size;
    }
    return;
  }
  for (let i = 0; i < len; i += 1) {
    target[i] = Math.random() * 2 - 1; // NOSONAR — Web Crypto unavailable fallback
  }
}

const SOUND_TYPES = [
  {
    id: "wooden_click",
    name: "Wooden Click",
    description: "Soft, warm wooden metronome tap"
  },
  {
    id: "bell",
    name: "Mid-Tone Bell",
    description: "Clear, resonant bell with gentle sustain"
  },
  {
    id: "soft_pulse",
    name: "Soft Pulse",
    description: "Smooth, rounded electronic pulse"
  },
  {
    id: "rain_drop",
    name: "Grandfather Clock",
    description: "Wooden pendulum tick‑tock of an old grandfather clock"
  },
  {
    id: "singing_bowl",
    name: "Singing Bowl",
    description: "Warm harmonic tone inspired by Tibetan bowls"
  }
];

// Bump this when you want to force a one-time migration of existing users' sound prefs.
const PREFS_VERSION = "2";
const PREFS_KEY = "mindfulpath_sound_prefs";
const PREFS_VERSION_KEY = "mindfulpath_sound_prefs_version";
const DEFAULT_PREFS = { soundType: "wooden_click", volume: 0.5, voiceVolume: 0.55, celebrationSound: true };

// Get / set user preferences
function getSoundPrefs() {
  try {
    const storedVersion = localStorage.getItem(PREFS_VERSION_KEY);
    if (storedVersion !== PREFS_VERSION) {
      // Force every existing user back to wooden_click while preserving their volume.
      const stored = localStorage.getItem(PREFS_KEY);
      const prev = stored ? JSON.parse(stored) : {};
      const migrated = {
        ...DEFAULT_PREFS,
        volume: typeof prev.volume === "number" ? prev.volume : DEFAULT_PREFS.volume,
        voiceVolume: typeof prev.voiceVolume === "number" ? prev.voiceVolume : DEFAULT_PREFS.voiceVolume,
        celebrationSound: typeof prev.celebrationSound === "boolean"
          ? prev.celebrationSound
          : DEFAULT_PREFS.celebrationSound,
      };
      localStorage.setItem(PREFS_KEY, JSON.stringify(migrated));
      localStorage.setItem(PREFS_VERSION_KEY, PREFS_VERSION);
      return migrated;
    }
    const stored = localStorage.getItem(PREFS_KEY);
    if (stored) {
      // Backfill for returning users whose stored blob predates celebrationSound.
      const parsed = JSON.parse(stored);
      return { ...DEFAULT_PREFS, ...parsed };
    }
  } catch (e) { /* parse error — use defaults */ }
  return { ...DEFAULT_PREFS };
}

function setSoundPrefs(prefs) {
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  localStorage.setItem(PREFS_VERSION_KEY, PREFS_VERSION);
}

// ============ SOUND GENERATORS ============
// Each function plays a single click on a given AudioContext at a given time/side/volume

function playWoodenClick(ctx, now, vol, panner) {
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(vol * 2.1, now);
  masterGain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);

  const bodyOsc = ctx.createOscillator();
  bodyOsc.type = "sine";
  bodyOsc.frequency.setValueAtTime(280, now);
  bodyOsc.frequency.exponentialRampToValueAtTime(140, now + 0.08);
  const bodyGain = ctx.createGain();
  bodyGain.gain.setValueAtTime(vol * 0.9, now);
  bodyGain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);

  const clickOsc = ctx.createOscillator();
  clickOsc.type = "triangle";
  clickOsc.frequency.setValueAtTime(800, now);
  clickOsc.frequency.exponentialRampToValueAtTime(300, now + 0.04);
  const clickGain = ctx.createGain();
  clickGain.gain.setValueAtTime(vol * 0.5, now);
  clickGain.gain.exponentialRampToValueAtTime(0.001, now + 0.06);

  const bufferSize = ctx.sampleRate * 0.05;
  const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = noiseBuffer.getChannelData(0);
  fillWhiteNoise(data);
  for (let i = 0; i < bufferSize; i++) {
    data[i] *= Math.pow(1 - i / bufferSize, 3);
  }
  const noiseSrc = ctx.createBufferSource();
  noiseSrc.buffer = noiseBuffer;
  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = "bandpass";
  noiseFilter.frequency.value = 600;
  noiseFilter.Q.value = 2;
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(vol * 0.25, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.06);

  bodyOsc.connect(bodyGain);
  bodyGain.connect(masterGain);
  clickOsc.connect(clickGain);
  clickGain.connect(masterGain);
  noiseSrc.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(masterGain);
  masterGain.connect(panner);
  panner.connect(ctx.destination);

  bodyOsc.start(now); bodyOsc.stop(now + 0.18);
  clickOsc.start(now); clickOsc.stop(now + 0.06);
  noiseSrc.start(now); noiseSrc.stop(now + 0.06);
}

function playBell(ctx, now, vol, panner) {
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(vol * 2.1, now);
  masterGain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);

  // Fundamental
  const fund = ctx.createOscillator();
  fund.type = "sine";
  fund.frequency.value = 294;
  const fundGain = ctx.createGain();
  fundGain.gain.setValueAtTime(vol * 0.75, now);
  fundGain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);

  // 2nd harmonic
  const h2 = ctx.createOscillator();
  h2.type = "sine";
  h2.frequency.value = 588;
  const h2Gain = ctx.createGain();
  h2Gain.gain.setValueAtTime(vol * 0.3, now);
  h2Gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

  // 3rd harmonic (bell shimmer)
  const h3 = ctx.createOscillator();
  h3.type = "sine";
  h3.frequency.value = 882;
  const h3Gain = ctx.createGain();
  h3Gain.gain.setValueAtTime(vol * 0.15, now);
  h3Gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

  // Strike transient
  const strike = ctx.createOscillator();
  strike.type = "triangle";
  strike.frequency.setValueAtTime(1100, now);
  strike.frequency.exponentialRampToValueAtTime(294, now + 0.01);
  const strikeGain = ctx.createGain();
  strikeGain.gain.setValueAtTime(vol * 0.4, now);
  strikeGain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);

  fund.connect(fundGain); fundGain.connect(masterGain);
  h2.connect(h2Gain); h2Gain.connect(masterGain);
  h3.connect(h3Gain); h3Gain.connect(masterGain);
  strike.connect(strikeGain); strikeGain.connect(masterGain);
  masterGain.connect(panner);
  panner.connect(ctx.destination);

  fund.start(now); fund.stop(now + 0.6);
  h2.start(now); h2.stop(now + 0.4);
  h3.start(now); h3.stop(now + 0.3);
  strike.start(now); strike.stop(now + 0.04);
}

function playSoftPulse(ctx, now, vol, panner) {
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.001, now);
  masterGain.gain.linearRampToValueAtTime(vol * 2.0, now + 0.04);
  masterGain.gain.linearRampToValueAtTime(vol * 1.35, now + 0.08);
  masterGain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

  const osc = ctx.createOscillator();
  osc.type = "sine";
  osc.frequency.value = 320;

  const sub = ctx.createOscillator();
  sub.type = "sine";
  sub.frequency.value = 160;
  const subGain = ctx.createGain();
  subGain.gain.setValueAtTime(vol * 0.3, now);
  subGain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

  osc.connect(masterGain);
  sub.connect(subGain); subGain.connect(masterGain);
  masterGain.connect(panner);
  panner.connect(ctx.destination);

  osc.start(now); osc.stop(now + 0.25);
  sub.start(now); sub.stop(now + 0.2);
}

function playRainDrop(ctx, now, vol, panner) {
  // Grandfather-clock pendulum tick: a crisp, wooden mechanical click
  // with a brief resonant tail — evocative of a wooden clock case.
  // Left side plays first = higher "tick"; right = lower "tock".
  const isRight = (panner.pan?.value ?? 0) > 0;
  const pitchShift = isRight ? 1.0 : 1.15; // left (first) = higher

  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(vol * 2.1, now);
  masterGain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);

  // 1) Sharp high-mid click — the escapement biting (makes it feel crisp)
  const click = ctx.createOscillator();
  click.type = "square";
  click.frequency.setValueAtTime(2200 * pitchShift, now);
  click.frequency.exponentialRampToValueAtTime(800 * pitchShift, now + 0.012);
  const clickGain = ctx.createGain();
  clickGain.gain.setValueAtTime(vol * 0.55, now);
  clickGain.gain.exponentialRampToValueAtTime(0.001, now + 0.025);

  // 2) Wooden body — trimmer than before to avoid "dulled" feel
  const thud = ctx.createOscillator();
  thud.type = "triangle";
  thud.frequency.setValueAtTime(220 * pitchShift, now);
  thud.frequency.exponentialRampToValueAtTime(120 * pitchShift, now + 0.04);
  const thudGain = ctx.createGain();
  thudGain.gain.setValueAtTime(vol * 0.55, now);
  thudGain.gain.exponentialRampToValueAtTime(0.001, now + 0.06);

  // 3) Bright noise transient — the crack of wood / gear scrape
  const bufSize = Math.floor(ctx.sampleRate * 0.03);
  const noiseBuf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
  const nd = noiseBuf.getChannelData(0);
  fillWhiteNoise(nd);
  for (let i = 0; i < bufSize; i++) {
    nd[i] *= Math.pow(1 - i / bufSize, 1.5);
  }
  const nSrc = ctx.createBufferSource();
  nSrc.buffer = noiseBuf;
  const nFilt = ctx.createBiquadFilter();
  nFilt.type = "bandpass";
  nFilt.frequency.value = 2800 * pitchShift;
  nFilt.Q.value = 0.9;
  const nGain = ctx.createGain();
  nGain.gain.setValueAtTime(vol * 0.4, now);
  nGain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);

  // 4) Very brief wooden ring — short, not muddy
  const res = ctx.createOscillator();
  res.type = "sine";
  res.frequency.value = 420 * pitchShift;
  const resGain = ctx.createGain();
  resGain.gain.setValueAtTime(vol * 0.12, now + 0.008);
  resGain.gain.exponentialRampToValueAtTime(0.001, now + 0.14);

  click.connect(clickGain); clickGain.connect(masterGain);
  thud.connect(thudGain); thudGain.connect(masterGain);
  nSrc.connect(nFilt); nFilt.connect(nGain); nGain.connect(masterGain);
  res.connect(resGain); resGain.connect(masterGain);
  masterGain.connect(panner);
  panner.connect(ctx.destination);

  click.start(now); click.stop(now + 0.03);
  thud.start(now); thud.stop(now + 0.08);
  nSrc.start(now); nSrc.stop(now + 0.04);
  res.start(now + 0.008); res.stop(now + 0.16);
}

function playSingingBowl(ctx, now, vol, panner) {
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.001, now);
  masterGain.gain.linearRampToValueAtTime(vol * 1.85, now + 0.06);
  masterGain.gain.exponentialRampToValueAtTime(0.001, now + 0.8);

  // Fundamental (warm)
  const f1 = ctx.createOscillator();
  f1.type = "sine";
  f1.frequency.value = 340;
  const f1g = ctx.createGain();
  f1g.gain.setValueAtTime(vol * 0.75, now);
  f1g.gain.exponentialRampToValueAtTime(0.001, now + 0.7);

  // 2nd partial
  const f2 = ctx.createOscillator();
  f2.type = "sine";
  f2.frequency.value = 680;
  const f2g = ctx.createGain();
  f2g.gain.setValueAtTime(vol * 0.35, now);
  f2g.gain.exponentialRampToValueAtTime(0.001, now + 0.5);

  // 3rd partial with slight detune for warmth
  const f3 = ctx.createOscillator();
  f3.type = "sine";
  f3.frequency.value = 1025;
  const f3g = ctx.createGain();
  f3g.gain.setValueAtTime(vol * 0.17, now);
  f3g.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

  // Subtle wobble (amplitude modulation)
  const lfo = ctx.createOscillator();
  lfo.type = "sine";
  lfo.frequency.value = 5;
  const lfoGain = ctx.createGain();
  lfoGain.gain.value = vol * 0.07;
  lfo.connect(lfoGain);
  lfoGain.connect(masterGain.gain);

  f1.connect(f1g); f1g.connect(masterGain);
  f2.connect(f2g); f2g.connect(masterGain);
  f3.connect(f3g); f3g.connect(masterGain);
  masterGain.connect(panner);
  panner.connect(ctx.destination);

  f1.start(now); f1.stop(now + 0.8);
  f2.start(now); f2.stop(now + 0.6);
  f3.start(now); f3.stop(now + 0.4);
  lfo.start(now); lfo.stop(now + 0.8);
}

// Dispatcher: play one click of the chosen sound type
function playSound(ctx, soundType, side, vol) {
  const now = ctx.currentTime;
  const panner = ctx.createStereoPanner();
  panner.pan.value = side === "left" ? -1 : 1;

  switch (soundType) {
    case "bell":
      playBell(ctx, now, vol, panner);
      break;
    case "soft_pulse":
      playSoftPulse(ctx, now, vol, panner);
      break;
    case "rain_drop":
      playRainDrop(ctx, now, vol, panner);
      break;
    case "singing_bowl":
      playSingingBowl(ctx, now, vol, panner);
      break;
    case "wooden_click":
    default:
      playWoodenClick(ctx, now, vol, panner);
      break;
  }
}

// Pick the gentlest / softest voice available (prefer younger-sounding female English voices)
function pickGentleVoice() {
  try {
    const voices = window.speechSynthesis?.getVoices?.() || [];
    if (!voices.length) return null;
    // Ordered from youngest / kindest to more mature. Prefer "Natural"/"Enhanced" variants.
    const preferredNames = [
      "Microsoft Jenny Online (Natural) - English (United States)",
      "Microsoft Aria Online (Natural) - English (United States)",
      "Google UK English Female",
      "Google US English",
      "Ava (Enhanced)",
      "Ava",
      "Allison",
      "Nicky",
      "Microsoft Jenny",
      "Microsoft Aria",
      "Microsoft Zira",
      "Samantha",   // mature — kept as later fallback
      "Victoria",
      "Karen",
      "Moira",
      "Tessa",
      "Serena"
    ];
    for (const name of preferredNames) {
      const v = voices.find(x => x.name === name);
      if (v) return v;
    }
    // Secondary: any voice whose name *contains* one of these words (handles locale suffixes)
    const keywords = ["Jenny", "Aria", "Ava", "Allison", "Nicky", "Female", "Google UK"];
    for (const kw of keywords) {
      const v = voices.find(x => x.name.includes(kw) && /^en[-_]/i.test(x.lang));
      if (v) return v;
    }
    // Fallback: first English voice
    return voices.find(v => /^en[-_]/i.test(v.lang)) || voices[0];
  } catch (e) {
    return null;
  }
}

// ============================================================================
// BREATH-CUE AUDIO FILES
// ============================================================================
// Real human voice recordings (ElevenLabs "Elise" — same voice used for
// task narration) that replace the browser's speechSynthesis. The synth-
// based cues were unreliable on iOS Safari and never sounded fully human;
// these prerecorded MP3s play through the same Web Audio pipeline as the
// bilateral tones, so they're reliable everywhere, respect the user's
// volume slider, and sound natural.
//
// Each entry is either:
//   • a single string  — plays as one clip
//   • an array of strings — plays each clip in order with `TWO_PHRASE_PAUSE_MS`
//     of silence between them (used for two-phrase cues like "Breathe in."
//     + "Breathe out." where the pause is the point of the cue)
//
// Current phrases (regenerated Feb 2026 in Elise's voice):
//   cue-01: "Take a deep breath."
//   cue-02: "One deep breath."
//   cue-03: "Breathe slowly."
//   cue-04: "A long, slow breath."
//   cue-05: "Breathe in and let it out."
//   cue-06: "Breathe in." · 1.5s pause · "Breathe out."
//
// New files can be dropped in by appending here — the sampler rotates
// through whatever's in the list and never plays the same one twice in
// a row.
// ============================================================================
const TWO_PHRASE_PAUSE_MS = 1500;
const BREATH_CUE_FILES = [
  "/audio/breath-cues/cue-01.mp3",
  "/audio/breath-cues/cue-02.mp3",
  "/audio/breath-cues/cue-03.mp3",
  "/audio/breath-cues/cue-04.mp3",
  "/audio/breath-cues/cue-05.mp3",
  ["/audio/breath-cues/cue-06-a.mp3", "/audio/breath-cues/cue-06-b.mp3"],
];

// Per-session state — reset when a new intervention flow begins.
// History of the last few indices picked. A new draw excludes every index
// still in the history window, guaranteeing no cue repeats within N plays.
let recentCueHistory = [];
// With 5 cues in the pool, a history window of 3 means the next draw will
// choose from 2 remaining cues — enough randomness without repeats.
const CUE_HISTORY_WINDOW = 3;
// Preloaded AudioBuffer cache keyed by file path so we don't re-fetch+decode
// the same clip every time. First playback triggers the fetch + decode; all
// later playbacks pull from the cache and start instantly.
const breathCueBufferCache = new Map();

async function loadCueBuffer(ctx, path) {
  if (breathCueBufferCache.has(path)) return breathCueBufferCache.get(path);
  const res = await fetch(path);
  const arrayBuf = await res.arrayBuffer();
  // decodeAudioData — use the promise form where available, fall back to the
  // older callback form for older Safari.
  const buffer = await new Promise((resolve, reject) => {
    try {
      const p = ctx.decodeAudioData(arrayBuf, resolve, reject);
      if (p && typeof p.then === "function") p.then(resolve, reject);
    } catch (e) { reject(e); }
  });
  breathCueBufferCache.set(path, buffer);
  return buffer;
}

function pickBreathCuePath() {
  // Exclude every index still in the recent-history window so no cue repeats
  // within CUE_HISTORY_WINDOW plays. Guard in case the pool shrinks below
  // the window (shouldn't happen with 9 cues + window=4, but defensive).
  let pool = BREATH_CUE_FILES
    .map((_, i) => i)
    .filter((i) => !recentCueHistory.includes(i));
  if (pool.length === 0) {
    // Extreme fallback — never happens with the current pool size, but keeps
    // the function safe if someone drops the pool below the window size.
    const last = recentCueHistory[recentCueHistory.length - 1];
    pool = BREATH_CUE_FILES.map((_, i) => i).filter((i) => i !== last);
  }
  const chosen = pool[Math.floor(secureRandomFloat() * pool.length)];
  recentCueHistory = [...recentCueHistory, chosen].slice(-CUE_HISTORY_WINDOW);
  return BREATH_CUE_FILES[chosen];
}

// Reset the breath-cue state — called whenever the user starts a new intervention.
function resetBreathCues() {
  recentCueHistory = [];
}

// Eagerly warm the cache for the first cue so the first playback of the
// session has zero latency. Safe to call during a user gesture (tap Play).
async function primeBreathCues() {
  try {
    const ctx = getOrCreateAudioContext();
    if (!ctx) return;
    // Preload the first two clips so the second session also plays
    // instantly. Multi-phrase entries are arrays — flatten to a plain
    // path list before priming.
    const asPaths = (entry) => (Array.isArray(entry) ? entry : [entry]);
    const seeds = [
      ...asPaths(BREATH_CUE_FILES[0]),
      ...asPaths(BREATH_CUE_FILES[1] || BREATH_CUE_FILES[0]),
    ];
    await Promise.all(seeds.map((p) => loadCueBuffer(ctx, p).catch(() => {})));
  } catch (e) { /* ignore — fall back to on-demand fetch */ }
}

// ============================================================================
// Celebration chime — soft, regal fanfare played alongside the confetti /
// fireworks visuals. A rising four-note arpeggio (C5 → E5 → G5 → C6) sits
// on top of a warm low C3 drone for ceremonial weight. Each note blends a
// sine fundamental with a gentle triangle-wave overtone so it feels a touch
// horn-like without being sharp. Scales with the user's volume slider and
// is gated by the `celebrationSound` pref.
// ============================================================================
function playCelebrationChime(userVolume = 1) {
  try {
    const prefs = getSoundPrefs();
    if (prefs.celebrationSound === false) return;
    const ctx = getOrCreateAudioContext();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      try { ctx.resume(); } catch (e) { /* ignore */ }
    }
    const clamp = (v) => Math.max(0, Math.min(1, v));
    // Keep under the bilateral bed so it feels like a flourish, not a fanfare.
    const vol = 0.22 * clamp(userVolume);
    const baseNow = ctx.currentTime;

    // Soft subwoofer-ish drone: low C3 sine, slow swell, long decay.
    // Gives the chime its regal "hall" weight without being boomy.
    const droneOsc = ctx.createOscillator();
    droneOsc.type = "sine";
    droneOsc.frequency.value = 130.81; // C3
    const droneGain = ctx.createGain();
    droneGain.gain.setValueAtTime(0.0001, baseNow);
    droneGain.gain.linearRampToValueAtTime(vol * 0.55, baseNow + 0.18);
    droneGain.gain.exponentialRampToValueAtTime(0.0001, baseNow + 1.4);
    droneOsc.connect(droneGain);
    droneGain.connect(ctx.destination);
    droneOsc.start(baseNow);
    droneOsc.stop(baseNow + 1.45);

    // Rising fanfare arpeggio: C5 → E5 → G5 → C6 (I–III–V–I, classic herald).
    // Each note = sine fundamental + quieter triangle overtone for a hint
    // of horn color.
    const notes = [
      { freq: 523.25, delay: 0.00 }, // C5
      { freq: 659.25, delay: 0.14 }, // E5
      { freq: 783.99, delay: 0.28 }, // G5
      { freq: 1046.5, delay: 0.42 }, // C6
    ];
    notes.forEach(({ freq, delay }) => {
      const t = baseNow + delay;

      // Sine fundamental — the core bell-like tone
      const sineOsc = ctx.createOscillator();
      sineOsc.type = "sine";
      sineOsc.frequency.value = freq;
      const sineGain = ctx.createGain();
      sineGain.gain.setValueAtTime(0.0001, t);
      sineGain.gain.linearRampToValueAtTime(vol, t + 0.035);
      sineGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.7);
      sineOsc.connect(sineGain); sineGain.connect(ctx.destination);
      sineOsc.start(t); sineOsc.stop(t + 0.75);

      // Triangle overtone — soft horn-ish warmth
      const triOsc = ctx.createOscillator();
      triOsc.type = "triangle";
      triOsc.frequency.value = freq;
      const triGain = ctx.createGain();
      triGain.gain.setValueAtTime(0.0001, t);
      triGain.gain.linearRampToValueAtTime(vol * 0.35, t + 0.035);
      triGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.55);
      triOsc.connect(triGain); triGain.connect(ctx.destination);
      triOsc.start(t); triOsc.stop(t + 0.6);

      // Octave shimmer — a whisper of sparkle over the top
      const shimOsc = ctx.createOscillator();
      shimOsc.type = "sine";
      shimOsc.frequency.value = freq * 2;
      const shimGain = ctx.createGain();
      shimGain.gain.setValueAtTime(0.0001, t);
      shimGain.gain.linearRampToValueAtTime(vol * 0.15, t + 0.03);
      shimGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
      shimOsc.connect(shimGain); shimGain.connect(ctx.destination);
      shimOsc.start(t); shimOsc.stop(t + 0.45);
    });

    // Final resolving chord — all four notes sustain together briefly on the
    // last beat. Quiet, just enough to feel conclusive.
    const chordT = baseNow + 0.56;
    [523.25, 659.25, 783.99, 1046.5].forEach((f) => {
      const o = ctx.createOscillator();
      o.type = "sine";
      o.frequency.value = f;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, chordT);
      g.gain.linearRampToValueAtTime(vol * 0.28, chordT + 0.08);
      g.gain.exponentialRampToValueAtTime(0.0001, chordT + 0.9);
      o.connect(g); g.connect(ctx.destination);
      o.start(chordT); o.stop(chordT + 0.95);
    });
  } catch (e) {
    logger.error("Celebration chime failed:", e);
  }
}

// ============================================================================
// Fireworks soundtrack — mimics real firework bursts: a low "whump" of the
// shell detonating, layered with crackly noise sparkle as the embers fall.
// Multiple overlapping bursts played over ~2.4s to match the visual show.
// Volume held to the same subtle ceiling as the regal chime so it feels
// celebratory, not startling. Gated by the `celebrationSound` pref.
// ============================================================================
function playFireworksSoundtrack(userVolume = 1) {
  try {
    const prefs = getSoundPrefs();
    if (prefs.celebrationSound === false) return;
    const ctx = getOrCreateAudioContext();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      try { ctx.resume(); } catch (e) { /* ignore */ }
    }
    const clamp = (v) => Math.max(0, Math.min(1, v));
    // A touch louder than the chime so the finale bursts land with more
    // presence. Still a gentle ceiling — not startling.
    const vol = 0.32 * clamp(userVolume);
    const baseNow = ctx.currentTime;

    // Build a 2-second mono white-noise buffer we can reuse for each shell.
    const noiseBuffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const noiseData = noiseBuffer.getChannelData(0);
    fillWhiteNoise(noiseData);

    // Fire a single "shell": a low boom + mid body + high crackly sparkle tail.
    const fireShell = (t, size = 1.0) => {
      // 1) LOW BOOM — filtered noise through a lowpass, short punchy envelope.
      const boomSrc = ctx.createBufferSource();
      boomSrc.buffer = noiseBuffer;
      const boomFilter = ctx.createBiquadFilter();
      boomFilter.type = "lowpass";
      boomFilter.frequency.setValueAtTime(240, t);
      boomFilter.frequency.exponentialRampToValueAtTime(80, t + 0.25);
      boomFilter.Q.value = 1.2;
      const boomGain = ctx.createGain();
      boomGain.gain.setValueAtTime(0.0001, t);
      boomGain.gain.linearRampToValueAtTime(vol * 1.1 * size, t + 0.01);
      boomGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      boomSrc.connect(boomFilter);
      boomFilter.connect(boomGain);
      boomGain.connect(ctx.destination);
      boomSrc.start(t); boomSrc.stop(t + 0.4);

      // 2) MID BODY — sine sub-tone, gives the boom more "thump" weight.
      const thump = ctx.createOscillator();
      thump.type = "sine";
      thump.frequency.setValueAtTime(140, t);
      thump.frequency.exponentialRampToValueAtTime(55, t + 0.2);
      const thumpGain = ctx.createGain();
      thumpGain.gain.setValueAtTime(0.0001, t);
      thumpGain.gain.linearRampToValueAtTime(vol * 0.9 * size, t + 0.015);
      thumpGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
      thump.connect(thumpGain); thumpGain.connect(ctx.destination);
      thump.start(t); thump.stop(t + 0.35);

      // 3) HIGH CRACKLE — noise pushed through a highpass, slightly delayed,
      //    decays gently to mimic falling embers / sparkle tail.
      const crackleT = t + 0.04;
      const crackleSrc = ctx.createBufferSource();
      crackleSrc.buffer = noiseBuffer;
      const crackleFilter = ctx.createBiquadFilter();
      crackleFilter.type = "highpass";
      crackleFilter.frequency.value = 4000;
      crackleFilter.Q.value = 0.7;
      const crackleGain = ctx.createGain();
      crackleGain.gain.setValueAtTime(0.0001, crackleT);
      crackleGain.gain.linearRampToValueAtTime(vol * 0.45 * size, crackleT + 0.02);
      crackleGain.gain.exponentialRampToValueAtTime(0.0001, crackleT + 0.55);
      crackleSrc.connect(crackleFilter);
      crackleFilter.connect(crackleGain);
      crackleGain.connect(ctx.destination);
      crackleSrc.start(crackleT); crackleSrc.stop(crackleT + 0.6);
    };

    // Schedule shells to roughly match the visual cadence (7-10 shells over
    // ~2.4s with clustering). Use a fixed-ish rhythm with jitter so bursts
    // overlap and don't feel metronomic.
    const shellCount = 9;
    for (let i = 0; i < shellCount; i += 1) {
      const t = baseNow + (i * 0.2) + secureRandomFloat() * 0.14;
      const size = 0.7 + secureRandomFloat() * 0.45; // varied intensity
      fireShell(t, size);
    }
  } catch (e) {
    logger.error("Fireworks soundtrack failed:", e);
  }
}


// Play a random breath cue through the Web Audio pipeline, scaled to the
// user's volume slider. Returns a Promise that resolves when playback ends
// (or immediately on error) so callers can chain cleanup.
//
// Two-phrase cues (an array of paths in BREATH_CUE_FILES) play each clip
// in order with `TWO_PHRASE_PAUSE_MS` of silence between them so lines
// like "Breathe in. · [pause] · Breathe out." land with the breath rhythm.
async function speakBreathCue(userVolume = 1) {
  // Returns a Promise that resolves when the cue has finished audibly
  // playing (or immediately on error / no-op). Callers that want to wait
  // for the cue to land before showing UI (like the distress check-in
  // modal) can `await` this; old call sites still work because they
  // just don't await it.
  try {
    const ctx = getOrCreateAudioContext();
    if (!ctx) return;
    // Resume the context if the browser auto-suspended it between sessions.
    if (ctx.state === "suspended") {
      try { await ctx.resume(); } catch (e) { /* ignore */ }
    }
    const entry = pickBreathCuePath();
    const paths = Array.isArray(entry) ? entry : [entry];

    for (let i = 0; i < paths.length; i += 1) {
      // Insert the inter-phrase pause BEFORE every clip except the first
      // so a two-phrase cue reads as: "Breathe in." · pause · "Breathe out."
      if (i > 0) {
        await new Promise((r) => setTimeout(r, TWO_PHRASE_PAUSE_MS));
      }
      await playOneCueClip(ctx, paths[i], userVolume);
    }
  } catch (e) {
    // Silent failure — don't break the intervention if the cue can't play.
    // Downgraded to `warn` (dev-console only, no error-reporter email) because
    // this is nearly always a transient network hiccup on mobile (WiFi ↔
    // cellular handoff, ad-blocker, brief backend blip). The intervention
    // itself continues without the cue — nothing the user or Sarah can act on.
    logger.warn("Breath cue playback failed:", e);
  }
}

// Play a single breath-cue MP3 through the tuned Web Audio chain. Extracted
// so `speakBreathCue` can sequence multiple phrases with a pause between.
async function playOneCueClip(ctx, path, userVolume) {
  const buffer = await loadCueBuffer(ctx, path);

  const source = ctx.createBufferSource();
  source.buffer = buffer;
  // Slight speed-up so the cues feel a touch snappier without sounding
  // rushed or chipmunked. 1.08× = ~8% faster, pitch shifts only slightly.
  source.playbackRate.value = 1.08;
  // Detune downward to clearly deepen the voice. With playbackRate at
  // 1.08× (~+133 cents), a detune of -200 cents lands the net pitch about
  // 2/3 of a semitone BELOW the original recording — noticeably lower in
  // the chest without sounding slowed or warbly.
  if ("detune" in source) {
    source.detune.value = -200;
  }

  // Low-shelf + gentle high-shelf EQ — adds clear body to the low-mids
  // and softens upper-mids/air so the voice feels warmer and deeper.
  const lowShelf = ctx.createBiquadFilter();
  lowShelf.type = "lowshelf";
  lowShelf.frequency.value = 260;
  lowShelf.gain.value = 6; // +6 dB under 260 Hz
  const highShelf = ctx.createBiquadFilter();
  highShelf.type = "highshelf";
  highShelf.frequency.value = 5000;
  highShelf.gain.value = -3; // -3 dB above 5 kHz

  // Voice volume is now an independent setting — the passed value IS the
  // absolute gain (0–1). The breath cue runs through a Web Audio chain
  // with a +6 dB low-shelf boost and the source MP3s are mastered hot,
  // so at the same slider value the cue measures noticeably louder
  // than the HTML-audio narration voice. A calibration constant
  // attenuates the cue path so both voices land at matching perceived
  // loudness when the user moves the single "Voice volume" slider.
  const BREATH_CUE_CALIBRATION = 0.55; // ≈ -5 dB to match HTML <audio> narration
  const gain = ctx.createGain();
  const clamp = (v) => Math.max(0, Math.min(1, v));
  gain.gain.value = clamp(userVolume) * BREATH_CUE_CALIBRATION;

  source.connect(lowShelf);
  lowShelf.connect(highShelf);
  highShelf.connect(gain);
  gain.connect(ctx.destination);

  // Wait for the source's `ended` event so callers know exactly when
  // the cue has finished playing. Belt-and-suspenders timeout uses the
  // decoded buffer duration in case the event is dropped (rare on
  // older Safari).
  await new Promise((resolve) => {
    let resolved = false;
    const done = () => { if (!resolved) { resolved = true; resolve(); } };
    source.onended = done;
    source.start(0);
    setTimeout(done, Math.ceil(((buffer.duration / 1.08) + 0.25) * 1000));
  });
}

// Shared AudioContext accessor so the breath cue and the bilateral tones
// use the same context. Handles the Safari webkit prefix.
//
// SILENT-SWITCH FIX (iOS Capacitor):
// WKWebView's Web Audio graph does NOT automatically respect the app's
// AVAudioSession `.playback` category, so bilateral tones and breath cues
// go silent when the user flips the mute switch — even though the HTML
// `<audio>` narration keeps playing. Two layers of fix here:
//   1. `navigator.audioSession.type = "playback"` — the WebKit-native
//      switch on iOS 16.4+ that tells the browser "this is media,
//      ignore the mute switch."
//   2. A silent looped HTML `<audio>` primer that runs alongside the
//      Web Audio context. HTML audio inherits the AVAudioSession's
//      `.playback` category, and keeping one playing forces WKWebView
//      to route its Web Audio through the same path — so the tones
//      and cues also ignore the mute switch on older iOS.
// The primer plays a ~1-second silent MP3 on loop at 0 volume, so
// nothing audible reaches the user; it's purely a routing hint.
let _cueAudioCtx = null;
let _silentPrimer = null;
// Tiny valid silent MP3 (~200 bytes) as a data-URI. Generated from an
// empty MPEG-1 Layer III frame; loops indefinitely at 0 volume so it
// is completely inaudible while keeping the audio session active.
const SILENT_MP3 =
  "data:audio/mpeg;base64,/+MYxAAAAANIAAAAAExBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV/+MYxDsAAANIAAAAAFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV/+MYxHYAAANIAAAAAFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";

function primeSilentAudioSession() {
  try {
    // iOS 16.4+ / modern Safari — the official knob.
    if (typeof navigator !== "undefined" && navigator.audioSession) {
      try { navigator.audioSession.type = "playback"; } catch (e) { /* ignore */ }
    }
    if (_silentPrimer) return;
    const el = document.createElement("audio");
    el.src = SILENT_MP3;
    el.loop = true;
    el.volume = 0;
    el.setAttribute("playsinline", "true");
    el.setAttribute("webkit-playsinline", "true");
    // Detach from the document — no visual, no accessibility surface.
    el.style.display = "none";
    document.body.appendChild(el);
    const p = el.play();
    if (p != null && typeof p.catch === "function") p.catch(() => { /* silent */ });
    _silentPrimer = el;
  } catch (e) {
    /* non-fatal — Web Audio still works, just may honour mute switch */
  }
}

function getOrCreateAudioContext() {
  if (_cueAudioCtx && _cueAudioCtx.state !== "closed") return _cueAudioCtx;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    _cueAudioCtx = new AC();
    // Route WKWebView's Web Audio through the playback audio session so
    // bilateral tones + breath cues ignore the physical mute switch.
    primeSilentAudioSession();
    return _cueAudioCtx;
  } catch (e) {
    return null;
  }
}


// Speak a short, gentle cue. Safe no-op when speech synthesis is unavailable.
//
// Hardened against two Chromium/WebKit quirks that make the voice cue unreliable
// after long bilateral sessions:
//   1. speechSynthesis goes into a silent paused state after ~15s of inactivity.
//      Calling resume() before speak() wakes it back up.
//   2. The voice list is populated asynchronously — on the very first page load
//      the list can be empty when we try to pick one. If so, defer and retry
//      once so the cue still fires.
function speakGentle(text, opts = {}) {
  const { retried = false, userVolume = 1 } = opts;
  try {
    const synth = window.speechSynthesis;
    if (!synth || typeof SpeechSynthesisUtterance === "undefined") return;

    // Wake the synth if Chromium's 15s auto-pause has kicked in.
    try { synth.resume(); } catch (e) { /* ignore */ }

    // If voices haven't loaded yet (common on fresh sessions/Safari), wait
    // briefly and try again. Avoids a dropped cue at the end of the first
    // bilateral session of the session.
    const voices = synth.getVoices();
    if ((!voices || voices.length === 0) && !retried) {
      setTimeout(() => speakGentle(text, { retried: true, userVolume }), 250);
      return;
    }

    // Cancel anything queued so the cue isn't delayed
    synth.cancel();

    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = 0.78;    // unhurried, breath-like cadence — slower than conversational
    utter.pitch = 1.05;   // subtle softness without cartoonishness
    // Voice is deliberately softer than the bilateral tones so it feels like
    // a gentle prompt rather than a directive. Scales with the user's volume
    // so turning the bilateral slider down also quiets the voice.
    const baseVoiceVolume = 0.38;
    const clamp = (v) => Math.max(0, Math.min(1, v));
    utter.volume = clamp(baseVoiceVolume * clamp(userVolume));
    const voice = pickGentleVoice();
    if (voice) utter.voice = voice;

    // Some browsers silently drop the first speak() after the auto-pause bug.
    // Retrying once on error makes the cue reliably audible.
    utter.onerror = () => {
      if (!retried) {
        setTimeout(() => speakGentle(text, { retried: true, userVolume }), 150);
      }
    };

    synth.speak(utter);

    // Belt and suspenders — call resume() again on the next tick in case the
    // queue got stuck between cancel() and speak().
    setTimeout(() => {
      try { synth.resume(); } catch (e) { /* ignore */ }
    }, 60);
  } catch (e) {
    logger.error("speakGentle failed:", e);
  }
}

// Keep-alive: Chromium's speechSynthesis silently drops into a paused state
// after ~15 seconds of inactivity. Since a full bilateral session runs ~45-50
// seconds, the voice cue at the end often fires on a paused queue and is
// silently lost. Callers start the keep-alive when the player begins and stop
// it when the player ends; the ticker pokes `resume()` every 10s so the queue
// stays awake through the whole session.
let speechKeepAliveId = null;
let speechPrimed = false;

// iOS Safari locks speechSynthesis until it's first called from a user gesture
// (the same rule Web Audio follows). Because our breath cue fires ~45s later
// inside a setInterval callback, iOS considers that no longer a user gesture
// and silently drops the audio. Calling this inside the Play button's onClick
// unlocks the synth with a near-silent, ultra-short utterance so the real
// breath cue plays cleanly at session end. One-shot — subsequent sessions in
// the same tab reuse the unlocked state.
function primeSpeechSynthesis() {
  try {
    if (speechPrimed) return;
    const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
    if (!synth || typeof SpeechSynthesisUtterance === "undefined") return;
    // A single space with volume 0 is enough to satisfy iOS's gesture check
    // without producing any audible output.
    const u = new SpeechSynthesisUtterance(" ");
    u.volume = 0;
    u.rate = 1;
    synth.speak(u);
    speechPrimed = true;
  } catch (e) { /* ignore */ }
}
function startSpeechKeepAlive() {
  try {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    stopSpeechKeepAlive();
    speechKeepAliveId = setInterval(() => {
      try { window.speechSynthesis.resume(); } catch (e) { /* ignore */ }
    }, 10000);
  } catch (e) { /* ignore */ }
}
function stopSpeechKeepAlive() {
  if (speechKeepAliveId) {
    clearInterval(speechKeepAliveId);
    speechKeepAliveId = null;
  }
}

// Some browsers populate voices asynchronously — prime the list early.
if (typeof window !== "undefined" && window.speechSynthesis) {
  try {
    window.speechSynthesis.getVoices();
    window.speechSynthesis.onvoiceschanged = () => {
      // Triggers internal cache refresh
      window.speechSynthesis.getVoices();
    };
  } catch (e) { /* ignore */ }
}

export { SOUND_TYPES, getSoundPrefs, setSoundPrefs, playSound, speakGentle, speakBreathCue, resetBreathCues, startSpeechKeepAlive, stopSpeechKeepAlive, primeSpeechSynthesis, primeBreathCues, playCelebrationChime, playFireworksSoundtrack, primeSilentAudioSession };
